import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { login } from '@/api/client';
import { keys, useInvalidate, useMe } from '@/api/hooks';
import { platform } from '@/platform';
import { useUi } from './store';
import { NavBar } from '@/components/NavBar';
import { Stars } from '@/components/Stars';
import { Toast } from '@/components/Toast';
import { HomeScreen } from '@/features/home/HomeScreen';
import { DailyScreen } from '@/features/daily/DailyScreen';
import { ReadingScreen } from '@/features/reading/ReadingScreen';
import { ShopScreen } from '@/features/shop/ShopScreen';
import { InventoryScreen } from '@/features/inventory/InventoryScreen';
import { DecksScreen } from '@/features/decks/DecksScreen';
import { ProfileScreen } from '@/features/profile/ProfileScreen';
import { HistoryScreen } from '@/features/profile/HistoryScreen';
import { OnboardingScreen } from '@/features/onboarding/OnboardingScreen';

const TABS = ['/', '/shop', '/inventory', '/profile'];

export function App() {
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [startParam, setStartParam] = useState<string | null>(null);
  const reduced = useUi((s) => s.reducedMotion);
  const showToast = useUi((s) => s.showToast);
  const invalidate = useInvalidate();
  const nav = useNavigate();
  const loc = useLocation();

  const loginStarted = useRef(false);
  useEffect(() => {
    if (loginStarted.current) return; // StrictMode вызывает эффекты дважды
    loginStarted.current = true;
    login().then((r) => { setStartParam(r.startParam ?? platform.getStartParam()); setAuthed(true); }).catch((e: Error) => setAuthError(e.message));
  }, []);

  useEffect(() => { document.body.classList.toggle('reduced', reduced); }, [reduced]);

  // Возврат с оплаты Robokassa (веб): /?pay=success|fail
  useEffect(() => {
    if (!authed) return;
    const pay = new URLSearchParams(location.search).get('pay');
    if (!pay) return;
    history.replaceState(null, '', location.pathname);
    if (pay === 'success') { showToast('Оплата прошла — начисляем ✨'); void invalidate(keys.me); setTimeout(() => void invalidate(keys.me), 4000); }
    else showToast('Оплата не завершена', true);
  }, [authed]);

  // Системная кнопка «назад» Telegram — только вне вкладок
  useEffect(() => {
    platform.setBackHandler(TABS.includes(loc.pathname) ? null : () => nav(-1));
    window.scrollTo(0, 0);
  }, [loc.pathname, nav]);

  const me = useMe(authed);

  if (authError) return <div className="empty"><div className="ic">🌑</div><b>Не удалось войти</b><br /><span className="dim">{authError}</span><br /><br /><button className="btn ghost" onClick={() => location.reload()}>Повторить</button></div>;
  if (!authed || !me.data) return <div className="boot"><div className="boot-card" /><div className="dim">Тасуем колоду…</div></div>;

  const user = me.data;
  if (!user.onboarded && loc.pathname !== '/onboarding') return <Navigate to="/onboarding" replace />;
  if (startParam === 'daily' && loc.pathname === '/') return <Navigate to="/daily" replace />;

  const showNav = TABS.includes(loc.pathname) || ['/decks', '/history'].includes(loc.pathname);

  return (
    <>
      <Stars />
      <Routes>
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/" element={<HomeScreen />} />
        <Route path="/daily" element={<DailyScreen />} />
        <Route path="/reading" element={<ReadingScreen />} />
        <Route path="/reading/:id" element={<ReadingScreen />} />
        <Route path="/shop" element={<ShopScreen />} />
        <Route path="/inventory" element={<InventoryScreen />} />
        <Route path="/decks" element={<DecksScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/history" element={<HistoryScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showNav && <NavBar />}
      <Toast />
    </>
  );
}
